import React, { useCallback, useContext, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { BatchesTable } from 'components/shipping/components/viewBatches/BatchesTable';
import {
    useBatchesSearch,
    useDownloadBatchFile,
} from 'components/shipping/components/viewBatches/queries/BatchQueries';
import { UserContext } from 'contexts/UserContext';

/**
 * Batches list for the logged-in user's facility (`GET /warehouse/v1/batches`).
 */
export function ViewBatches() {
    const { user } = useContext(UserContext);
    const currentFacilityId = user?.facilityID;

    const [downloadingBatchId, setDownloadingBatchId] = useState(null);

    const { data, error, isFetching, isLoading } = useBatchesSearch({ facilityId: currentFacilityId });

    const downloadBatchFileMutation = useDownloadBatchFile({
        onError: (err) => {
            toast.error(err?.message ?? 'Failed to download batch file');
        },
        onSettled: () => {
            setDownloadingBatchId(null);
        },
    });

    const handleDownloadFile = useCallback(
        (batchId) => {
            if (batchId === undefined || batchId === null || batchId === '') return;
            if (downloadBatchFileMutation.isPending) return;
            setDownloadingBatchId(batchId);
            downloadBatchFileMutation.mutate(batchId);
        },
        [downloadBatchFileMutation],
    );

    const rows = useMemo(() => data ?? [], [data]);
    const errorMessage = error?.message ?? '';
    const hasFacility = currentFacilityId !== undefined && currentFacilityId !== null && currentFacilityId !== '';

    return (
        <div className="flex min-h-screen flex-col">
            <div className="px-4 pt-4">
                <h1 className="text-xl font-semibold">View Batches</h1>
            </div>
            <div className="flex flex-1 flex-col px-4 pb-6 pt-4">
                {errorMessage ? (
                    <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                        {errorMessage}
                    </div>
                ) : null}
                <BatchesTable
                    data={rows}
                    downloadingBatchId={downloadingBatchId}
                    emptyMessage={hasFacility ? 'No batches found.' : 'Select a facility to view batches.'}
                    isFetching={isFetching}
                    isLoading={isLoading}
                    onDownloadFile={handleDownloadFile}
                />
            </div>
        </div>
    );
}

ViewBatches.displayName = 'ViewBatches';
